import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {  ERROR_MESSAGES, MARRIED_FAMILY_SITUATION_CODE } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import MgpFormInput from '../../elements/MgpFormInput/MgpFormInput';
import MgpFormSelect from '../../elements/MgpFormSelect/MgpFormSelect';
import { useFetchNomenclature } from '../../hooks/useFetchNomenclature';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { isValidDate } from '../../utils/utils';
import { getAllIndexByString } from '../../utils/utils';

type IdentityTemplateFormComponentProps = {
    register: UseFormRegister<any>;
    errors: any;
    setValue: UseFormSetValue<any>;
    watch: UseFormWatch<any>
}

export type IdentityTemplateFormData = {
  civilityCode: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  familySituationCode: string;
  birthPlace: string;
  birthName: string
}

export  const validationSchemaIdentityTemplate = Yup.object().shape({
    civilityCode: Yup.string()
     .required(ERROR_MESSAGES.requiredMsg),

    firstName: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .min(2, ERROR_MESSAGES.invalidMsg)
      .max(50, ERROR_MESSAGES.invalidMsg)
      .matches(/^[a-z '-]+$/i, ERROR_MESSAGES.invalidName)
      .test('invalidCombination', ERROR_MESSAGES.invalidMsg, (val)=> {
        let invalidCase = val?.includes('--') === true ;
        let isDashInTheMiddle;
        if(val){
          const indexList = getAllIndexByString(val, "-");
          isDashInTheMiddle = indexList.indexOf(0) !== -1 || indexList.indexOf(val.length-1) !== -1;
        }
        return !invalidCase && !isDashInTheMiddle;
      }),

    lastName: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .min(2, ERROR_MESSAGES.invalidMsg)
      .max(50, ERROR_MESSAGES.invalidMsg)
      .matches(/^[a-z '-]+$/i, ERROR_MESSAGES.invalidName)
      .test('invalidCombination', ERROR_MESSAGES.invalidMsg, (val)=> {
        let invalidCase = val?.includes('--') === true ;
        let isDashInTheMiddle;
        if(val){
          const indexList = getAllIndexByString(val, "-");
          isDashInTheMiddle = indexList.indexOf(0) !== -1 || indexList.indexOf(val.length-1) !== -1;
        }
        return !invalidCase && !isDashInTheMiddle;
      }),

    birthName: Yup.string()
      .when('familySituationCode', {
        is: (value:string)=> value === MARRIED_FAMILY_SITUATION_CODE,
        then: (schema) => schema.required(ERROR_MESSAGES.requiredMsg)
          .min(2, ERROR_MESSAGES.invalidMsg)
          .max(50, ERROR_MESSAGES.invalidMsg)
          .matches(/^[a-zA-Z '-]+$/i, ERROR_MESSAGES.invalidName)
          .test('invalidCombination', ERROR_MESSAGES.invalidMsg, (val)=> {
            let invalidCase = val?.includes('--') === true ;
            let isDashInTheMiddle;
            if(val){
              const indexList = getAllIndexByString(val, "-");
              isDashInTheMiddle = indexList.indexOf(0) !== -1 || indexList.indexOf(val.length-1) !== -1;
            }
            return !invalidCase && !isDashInTheMiddle;
          }),
        otherwise: Yup.string().transform(()=>  '').notRequired()

      }),

    birthDate: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
        let now = new Date();
        now.setHours(0,0,0,0);
        return val !== undefined && isValidDate(val) && new Date(val) < now;
    }),

    birthPlace: Yup.string()
      .required(ERROR_MESSAGES.requiredMsg)
      .min(3, ERROR_MESSAGES.invalidMsg)
      .max(30, ERROR_MESSAGES.invalidMsg),

    familySituationCode: Yup.string()
      .min(5, ERROR_MESSAGES.requiredMsg)
      .required(ERROR_MESSAGES.requiredMsg),
});



export default function IdentityTemplateFormComponent({register, errors, setValue, watch} : Readonly<IdentityTemplateFormComponentProps>) {

  const familySituationNomenclature = useFetchNomenclature("familySituation");
  const civilityNomenclature = useFetchNomenclature("civility");

  
  const {
    state: { updatedProposition }
  } = useAppState();

  useEffect(() => {
      if(familySituationNomenclature && updatedProposition?.familySituationCode) {
        setValue("familySituationCode", updatedProposition?.familySituationCode);
      }
      if(civilityNomenclature && updatedProposition?.civilityCode) {
        setValue("civilityCode", updatedProposition?.civilityCode);
      }
  }, [familySituationNomenclature, civilityNomenclature]);

  useEffect(()=> { 
    if(watch("familySituationCode") !== MARRIED_FAMILY_SITUATION_CODE && watch('birthName')?.length !== 0){
      setValue('birthName', '')
    }
    }, [watch("familySituationCode")])
    
   return (             
        <div className='form-row page-content'>
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
              {/* <h2 className='card-title bleu'>Mon identité : </h2> */}
              <div className='form-row'>
                <div className="form-group mw-100">
                  <MgpFormSelect 
                    id="civilityCode"
                    labelFontWeight='regular'
                    data={civilityNomenclature.options}
                    register={register}
                    errors={errors}
                    label='Civilité'
                    required={true}
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className="form-group mw-100">
                  <MgpFormInput
                    label="Prénom"
                    id="firstName"
                    labelFontWeight='regular'
                    register={register} 
                    errors={errors}
                    required={true}
                    autoCapitalize='characters'
                  />
                </div>
              </div>

              <div className='form-row'>
                <div className="form-group mw-100">
                  <MgpFormInput
                    label="Nom"
                    id="lastName"
                    labelFontWeight='regular'
                    register={register} 
                    errors={errors}
                    required={true}
                    autoCapitalize='characters'
                  />
                </div>
              </div>
                       
            {watch('familySituationCode') === MARRIED_FAMILY_SITUATION_CODE &&
              <div className='form-row'>
                <div className="form-group mw-100">
                  <MgpFormInput
                      label="Nom de naissance"
                      id="birthName"
                      labelFontWeight='regular'
                      register={register} 
                      errors={errors}
                      required={true}
                      maxLength={39}
                      autoCapitalize='characters'
                  />
                </div>
              </div>
            } 
            
          </div>
          
          <div className='col-flex-4 bg-blanc shadow-noir p-lg m-y-xs'>
            <div className='pb-md'>
              <div className='form-row'>
                <div className="form-group mw-100">
                    <MgpFormInput
                        label="Date de naissance"
                        id="birthDate"
                        labelFontWeight='medium'
                        type='date'
                        register={register}
                        errors={errors}
                        readOnly
                    />
                </div>
              </div>  

              <div className='form-row'>
                <div className="form-group mw-100">
                    <MgpFormInput
                        label="Lieu de naissance"
                        id="birthPlace"
                        labelFontWeight='regular'
                        register={register} 
                        errors={errors}
                        required={true}
                        placeholder='Ex: PARIS'
                        autoCapitalize='characters'
                        maxLength={30}
                        


                    />
                </div>
              </div>  
              
              <div className='form-row'>
                <div className="form-group mw-100">
                  <MgpFormSelect 
                    id="familySituationCode" 
                    labelFontWeight='regular' 
                    data={familySituationNomenclature.options}
                    label="Situation familiale" 
                    register={register} 
                    errors={errors} 
                    required={true}
                  />
                </div>
              </div>                
            </div>
             
            <div className="mt-xl">
              <p className='text fw-medium'><span className='fushia'>(*)</span> Champs obligatoires</p>
            </div>
          </div>
        </div>
    )
}
